/**
 * Cálculo de rescisão trabalhista - CLT brasileira
 * Baseado nas regras vigentes (Reforma Trabalhista 2017 + atualizações).
 *
 * @param {Object} input
 *   salary: salário bruto mensal (R$)
 *   admission: string 'YYYY-MM-DD'
 *   dismissal: string 'YYYY-MM-DD'
 *   reason: 'sem-justa-causa' | 'pedido' | 'acordo' | 'justa-causa' | 'termino-contrato'
 *   noticeType: 'indenizado' | 'trabalhado' | 'nenhum'
 *   fgtsBalance: saldo total depositado ao longo do contrato (R$, opcional)
 *   vacationOverdue: dias de férias vencidas não gozadas (0-30)
 *   dependents: número de dependentes (para IRRF aproximado - simplificado)
 */
export function calcRescisao(input) {
  const salary = Number(input.salary) || 0;
  const adm = new Date(input.admission);
  const dis = new Date(input.dismissal);
  const reason = input.reason || 'sem-justa-causa';
  const noticeType = input.noticeType || 'indenizado';
  const fgtsBalance = Number(input.fgtsBalance) || 0;
  const vacationOverdue = Math.min(30, Math.max(0, Number(input.vacationOverdue) || 0));

  if (!salary || isNaN(adm.getTime()) || isNaN(dis.getTime()) || dis < adm) {
    return { error: 'Preencha corretamente salário, admissão e data de saída (posterior à admissão).' };
  }

  // Time worked (in years for notice, months for proportional)
  const msPerDay = 86400000;
  const daysWorked = Math.floor((dis - adm) / msPerDay) + 1;
  const yearsCompleted = Math.floor(daysWorked / 365);

  // Days remaining in month of dismissal (saldo de salário)
  const dayOfMonth = dis.getDate();

  // Number of full/started months in the current year (for 13º and férias proporcionais)
  // Reforma trabalhista: 15 dias ou mais = mês integral
  const monthOfDismissal = dis.getMonth(); // 0-11
  const dayThreshold = dayOfMonth >= 15 ? 1 : 0;
  const proportionalMonths13th = monthOfDismissal + dayThreshold;

  // Aquisitive vacation period (last 12 months proportional)
  // count months worked since last anniversary of admission
  const anniv = new Date(adm);
  anniv.setFullYear(dis.getFullYear());
  if (anniv > dis) anniv.setFullYear(dis.getFullYear() - 1);
  const monthsSinceAnniv = (dis.getFullYear() - anniv.getFullYear()) * 12 + (dis.getMonth() - anniv.getMonth()) + (dis.getDate() >= 15 ? 1 : 0);
  const proportionalVacMonths = Math.min(12, Math.max(0, monthsSinceAnniv));

  // Aviso prévio (Lei 12.506/2011): 30 dias + 3 dias por ano completo (máximo 90)
  const noticeExtraDays = Math.min(60, Math.max(0, yearsCompleted * 3));
  const noticeTotalDays = 30 + noticeExtraDays;

  // ---- Verbas ----
  const daily = salary / 30;
  const items = [];

  // Saldo de salário (dias trabalhados no mês de saída)
  const saldo = daily * dayOfMonth;
  items.push({ label: 'Saldo de salário', detail: `${dayOfMonth} dias × R$ ${daily.toFixed(2)}`, value: saldo });

  // 13º proporcional (todas as modalidades exceto justa causa)
  const thirteenth = (salary / 12) * proportionalMonths13th;
  if (reason !== 'justa-causa') {
    items.push({ label: '13º salário proporcional', detail: `${proportionalMonths13th}/12 avos`, value: thirteenth });
  }

  // Férias vencidas + 1/3 (devidas em todos os casos, exceto se já pagas)
  if (vacationOverdue > 0) {
    const vacBase = (salary / 30) * vacationOverdue;
    const vacTotal = vacBase + vacBase / 3;
    items.push({ label: `Férias vencidas (${vacationOverdue} dias) + 1/3`, detail: `R$ ${vacBase.toFixed(2)} + 1/3`, value: vacTotal });
  }

  // Férias proporcionais + 1/3 (exceto justa causa)
  if (reason !== 'justa-causa' && proportionalVacMonths > 0) {
    const propVacBase = (salary / 12) * proportionalVacMonths;
    const propVac = propVacBase + propVacBase / 3;
    items.push({ label: 'Férias proporcionais + 1/3', detail: `${proportionalVacMonths}/12 avos`, value: propVac });
  }

  // Aviso prévio (sem justa causa e término antecipado do empregador)
  if (reason === 'sem-justa-causa' && noticeType !== 'nenhum') {
    const noticeValue = daily * noticeTotalDays;
    items.push({
      label: `Aviso prévio ${noticeType === 'indenizado' ? 'indenizado' : 'trabalhado'}`,
      detail: `${noticeTotalDays} dias (30 base + ${noticeExtraDays} por tempo de serviço)`,
      value: noticeType === 'indenizado' ? noticeValue : 0,
    });
    // 13º incidente sobre aviso indenizado + férias sobre aviso indenizado
    if (noticeType === 'indenizado') {
      const thirteenthOnNotice = (salary / 12) * (noticeTotalDays / 30);
      items.push({ label: 'Reflexo do aviso no 13º', detail: `${(noticeTotalDays / 30).toFixed(2)}/12`, value: thirteenthOnNotice });
      const vacOnNotice = (salary / 12) * (noticeTotalDays / 30);
      const vacOnNoticeTotal = vacOnNotice + vacOnNotice / 3;
      items.push({ label: 'Reflexo do aviso nas férias + 1/3', detail: `${(noticeTotalDays / 30).toFixed(2)}/12`, value: vacOnNoticeTotal });
    }
  } else if (reason === 'acordo' && noticeType === 'indenizado') {
    // Acordo: aviso prévio indenizado com 50%
    const halfNotice = (daily * noticeTotalDays) / 2;
    items.push({ label: 'Aviso prévio indenizado (50% – acordo)', detail: `${noticeTotalDays} dias × 50%`, value: halfNotice });
  }

  // Multa FGTS
  if (reason === 'sem-justa-causa' && fgtsBalance > 0) {
    items.push({ label: 'Multa de 40% sobre FGTS', detail: `40% × R$ ${fgtsBalance.toFixed(2)}`, value: fgtsBalance * 0.4 });
  } else if (reason === 'acordo' && fgtsBalance > 0) {
    items.push({ label: 'Multa de 20% sobre FGTS (acordo)', detail: `20% × R$ ${fgtsBalance.toFixed(2)}`, value: fgtsBalance * 0.2 });
  }

  // FGTS a sacar
  if ((reason === 'sem-justa-causa' || reason === 'termino-contrato') && fgtsBalance > 0) {
    items.push({ label: 'Saque integral do FGTS', detail: `100% do saldo`, value: fgtsBalance });
  } else if (reason === 'acordo' && fgtsBalance > 0) {
    items.push({ label: 'Saque parcial do FGTS (80% – acordo)', detail: `80% do saldo`, value: fgtsBalance * 0.8 });
  }

  const total = items.reduce((s, i) => s + i.value, 0);

  const notes = [];
  if (reason === 'justa-causa') notes.push('Na dispensa por justa causa, o trabalhador perde direito ao 13º proporcional, férias proporcionais, aviso prévio, multa e saque do FGTS.');
  if (reason === 'pedido') notes.push('No pedido de demissão, você deve cumprir o aviso prévio (ou tê-lo descontado) e não tem direito ao saque do FGTS nem à multa de 40%.');
  if (reason === 'acordo') notes.push('No acordo (art. 484-A CLT), aviso prévio e multa do FGTS são pagos pela metade; saque do FGTS limitado a 80%; sem direito ao seguro-desemprego.');
  notes.push('Este é um cálculo estimado. Valores exatos dependem de horas extras, adicionais, comissões, contribuições previdenciárias e impostos. Consulte um advogado trabalhista.');

  return {
    items,
    total,
    daysWorked,
    yearsCompleted,
    notes,
    reasonLabel: {
      'sem-justa-causa': 'Demissão sem justa causa',
      'pedido': 'Pedido de demissão',
      'acordo': 'Acordo (art. 484-A CLT)',
      'justa-causa': 'Demissão por justa causa',
      'termino-contrato': 'Término de contrato por prazo determinado',
    }[reason],
  };
}

export function formatBRL(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}
